import React from 'react';
import { Card, CardContent } from '../../../../components/card';
import { Checkbox } from '../../../../components/checkbox';
import { BusinessHoursUIState } from '../BusinessProfile.data';
import { WEEK_DAYS } from '../BusinessProfile.const';
import { SimpleTimePicker } from './SimpleTimePicker';

interface BusinessHoursSectionProps {
    businessHours: BusinessHoursUIState[];
    onHoursChange: (day: string, hours: BusinessHoursUIState) => void;
}

export const BusinessHoursSection: React.FC<BusinessHoursSectionProps> = ({
    businessHours,
    onHoursChange,
}) => {
    const handleDayToggle = (day: string, isOpen: boolean) => {
        const hour = businessHours.find((h) => h.day === day);
        if (hour) {
            onHoursChange(day, {
                ...hour,
                is_open: isOpen,
                open_time: isOpen ? hour.open_time : hour.open_time,
                close_time: isOpen ? hour.close_time : hour.close_time,
            });
        }
    };

    const handleTimeChange = (day: string, field: 'open_time' | 'close_time', time: string) => {
        const hour = businessHours.find((h) => h.day === day);
        if (hour) {
            onHoursChange(day, {
                ...hour,
                [field]: time,
            });
        }
    };

    return (
        <Card className="border border-[#e2e8f0] dark:border-gray-700 bg-white dark:bg-gray-800 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] dark:shadow-[0px_1px_2px_0px_rgba(0,0,0,0.3)] rounded-[8px]">
            <CardContent className="px-[24px] py-[24.8px]">
                <h2 className="text-[24px] font-semibold text-[#020817] dark:text-white tracking-[-0.6px] mb-0">
                    Business Hours
                </h2>

                <div className="pt-[5px] pb-0">
                    <div className="bg-white dark:bg-gray-900 rounded-[6px] p-[11.8px]">
                        <div className="flex flex-col gap-3">
                    {WEEK_DAYS.map((dayInfo) => {
                        const hour = businessHours.find((h) => h.day === dayInfo.value);
                        if (!hour) return null;

                        return (
                            <div
                                key={dayInfo.value}
                                className={`flex items-center gap-2 min-w-0 ${
                                    hour.is_open ? 'h-[24.2px]' : 'h-[19.5px]'
                                }`}
                            >
                                <Checkbox
                                    checked={hour.is_open}
                                    onChange={(e) => handleDayToggle(dayInfo.value, e.target.checked)}
                                    className={`w-4 h-4 rounded-[2.5px] ${
                                        hour.is_open
                                            ? 'bg-[#00c49a] dark:bg-emerald-500 border-[#00c49a] dark:border-emerald-500 accent-[#00c49a]'
                                            : 'bg-white dark:bg-gray-900 border-[#767676] dark:border-gray-600'
                                    }`}
                                />
                                <div className="w-[36px] flex-shrink-0">
                                    <span className="text-[13px] font-normal text-[#020817] dark:text-gray-200 leading-[19.5px]">
                                        {dayInfo.short}
                                    </span>
                                </div>

                                {hour.is_open ? (
                                    <div className="flex-1 flex items-center gap-[6px] min-w-0">
                                        <SimpleTimePicker
                                            value={hour.open_time}
                                            onChange={(time) =>
                                                handleTimeChange(dayInfo.value, 'open_time', time)
                                            }
                                            className="flex-shrink-0"
                                        />
                                        <span className="text-[12px] text-[#64748b] dark:text-gray-400 leading-[18px] flex-shrink-0">-</span>
                                        <SimpleTimePicker
                                            value={hour.close_time}
                                            onChange={(time) =>
                                                handleTimeChange(dayInfo.value, 'close_time', time)
                                            }
                                            className="flex-shrink-0"
                                        />
                                    </div>
                                ) : (
                                    <span className="text-[12px] text-[#64748b] dark:text-gray-400 leading-[18px] flex-shrink-0">Closed</span>
                                )}
                            </div>
                        );
                    })}
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

